import { AgentExecutionError, LLMProviderError, UserInputError } from "./errors-CEDWDXew.js";
import { logIssue, logWarn } from "./logging-B7X3yL0T.js";
import { ApiClientError, ApiServerError } from "./errors-DG2FreDU.js";
import { z } from "zod";
import { getActiveSpan } from "@sentry/core";
import { APICallError, RetryError, tool } from "ai";

//#region src/internal/agents/tools/utils.ts
/**
* Standard response schema for all embedded agent tools.
* Tools return either an error message or the result data, never both.
*/
const AgentToolResponseSchema = z.object({
	error: z.string().optional().describe("Error message if the operation failed"),
	result: z.unknown().optional().describe("The successful result data")
});
function recordAgentToolResultCount(count) {
	if (typeof count !== "number" || !Number.isFinite(count)) return;
	try {
		getActiveSpan()?.setAttribute("gen_ai.tool.call.result.count", count);
	} catch {}
}
function recordExtractedAgentToolResultCount(result, getResultCount) {
	if (!getResultCount) return;
	try {
		recordAgentToolResultCount(getResultCount(result));
	} catch {}
}
/**
* Handles errors from agent tool execution and returns appropriate error messages.
*
* SECURITY: Only returns trusted error messages to prevent prompt injection.
* We trust: Sentry API errors, our own UserInputError messages, and system templates.
*/
function handleAgentToolError(error) {
	if (error instanceof UserInputError) {
		logWarn(error, {
			loggerScope: ["agent-tools", "user-input"],
			contexts: { agentTool: { errorType: "UserInputError" } }
		});
		return { error: `Input Error: ${error.message}. You may be able to resolve this by addressing the concern and trying again.` };
	}
	if (error instanceof LLMProviderError) {
		logWarn(error, {
			loggerScope: ["agent-tools", "llm-provider"],
			contexts: { agentTool: { errorType: "LLMProviderError" } }
		});
		return { error: `AI Provider Error: ${error.message}. This is a service availability issue that cannot be resolved by retrying.` };
	}
	if (error instanceof AgentExecutionError) {
		const eventIdPart$1 = error.eventId ? ` Event ID: ${error.eventId}.` : "";
		return { error: `AI Processing Error: the embedded agent failed unexpectedly.${eventIdPart$1} This is a system error that cannot be resolved by retrying the same agent step.` };
	}
	if (APICallError.isInstance(error) || RetryError.isInstance(error)) {
		const statusCode = APICallError.isInstance(error) ? error.statusCode : void 0;
		logWarn(error, {
			loggerScope: ["agent-tools", "api-call"],
			contexts: { agentTool: {
				errorType: APICallError.isInstance(error) ? "APICallError" : "RetryError",
				statusCode: statusCode ?? null
			} }
		});
		return { error: `AI Provider Error: ${error.message}. This is a service availability issue with the upstream AI provider. Other non-AI tools should still work.` };
	}
	if (error instanceof ApiClientError) {
		const message = error.toUserMessage();
		logWarn(message, {
			loggerScope: ["agent-tools", "api-client"],
			contexts: { agentTool: {
				errorType: error.name,
				status: error.status ?? null
			} }
		});
		return { error: `Input Error: ${message}. You may be able to resolve this by addressing the concern and trying again.` };
	}
	if (error instanceof ApiServerError) {
		const eventId$1 = logIssue(error);
		const statusText = error.status ? ` (${error.status})` : "";
		const eventIdPart$1 = eventId$1 ? ` Event ID: ${eventId$1}.` : "";
		return { error: `Server Error${statusText}: ${error.message}.${eventIdPart$1} This is a system error that cannot be resolved by retrying.` };
	}
	const eventId = logIssue(error);
	const eventIdPart = eventId ? ` Event ID: ${eventId}.` : "";
	return { error: `System Error: An unexpected error occurred.${eventIdPart} This is a system error that cannot be resolved by retrying.` };
}
/**
* Creates an embedded agent tool with automatic error handling and schema wrapping.
*
* This wrapper:
* - Maintains the same API as the AI SDK's tool() function
* - Automatically wraps the result schema with error/result structure
* - Handles all error types and returns them as structured responses
* - Preserves type inference from the original tool implementation
*
* @example
* ```typescript
* export function createMyTool(apiService: SentryApiService) {
*   return agentTool({
*     description: "My tool description",
*     parameters: z.object({ param: z.string() }),
*     execute: async (params) => {
*       // Tool implementation that might throw errors
*       const result = await apiService.someMethod(params);
*       return result; // Original return type preserved
*     }
*   });
* }
* ```
*/
function agentTool(config) {
	return tool({
		description: config.description,
		inputSchema: config.parameters,
		execute: async (input) => {
			try {
				const result = await config.execute(input);
				recordExtractedAgentToolResultCount(result, config.getResultCount);
				return { result };
			} catch (error) {
				return handleAgentToolError(error);
			}
		}
	});
}

//#endregion
export { agentTool, recordAgentToolResultCount };
//# sourceMappingURL=utils-DHwF0RI3.js.map